import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient, getErrorMessage } from '../../../api/client'
import { useOfflineFirstQuery } from '../../../hooks/useOfflineFirstQuery'

type SessionOption = { id: string; label: string }

export default function ClaimGeneratePage() {
  const navigate = useNavigate()
  const [selectedSession, setSelectedSession] = useState<string>('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data: sessions, isFetching } = useOfflineFirstQuery<SessionOption[]>({
    queryKey: ['billable-sessions'],
    cacheKey: 'sessions:completed:billable',
    staleTime: 1000 * 60,
    queryFn: async () => {
      const res = await apiClient.get('/api/v1/sessions/?status=completed&billable=true')
      return (res.data.results ?? res.data) as SessionOption[]
    },
  })

  const handleSubmit = async () => {
    if (!selectedSession) {
      setError('Select a completed session')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiClient.post('/api/v1/billing/claims/generate_from_session/', {
        session_id: selectedSession,
      })
      const claimId = res.data?.id || res.data?.claim_id
      navigate(`/app/billing/claims/${claimId}`)
    } catch (e) {
      setError(getErrorMessage(e))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-xl font-semibold text-gray-900">Generate Claim</h1>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
        <label className="text-sm font-medium text-gray-700">Completed Session</label>
        <select
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-primary-500 focus:ring-primary-500"
          value={selectedSession}
          onChange={(e) => setSelectedSession(e.target.value)}
        >
          <option value="">Select…</option>
          {sessions?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label ?? s.id}
            </option>
          ))}
        </select>
        {isFetching && <p className="text-xs text-gray-500">Loading sessions…</p>}
      </div>

      <div className="flex gap-2">
        <button
          disabled={submitting}
          onClick={handleSubmit}
          className="rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 disabled:opacity-60"
        >
          {submitting ? 'Generating…' : 'Generate Claim'}
        </button>
        <button
          onClick={() => navigate(-1)}
          className="rounded-md border border-gray-200 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
